// Controles de la capa logo de la marca de agua: opacidad + modo del logo
// (original / sin fondo / solo blanco). El cambio de modo (con su llamada al
// backend si hace falta) lo maneja el hook — acá solo se renderiza y avisa.
// Layout en columna: vive en la tarjeta lateral de desktop (280-300px) y en la
// barra de acciones de mobile, como los controles de Recortar.

import { Eraser, Loader2 } from 'lucide-react';
import type { SceneApi } from '../../pages/edicionGrafica/scene/useScene';
import type { WatermarkLogoMode } from '../../utils/watermarkConfig';
import { OpacitySlider } from './OpacitySlider';

interface Props {
  api: SceneApi;
  mode: WatermarkLogoMode;
  /** true mientras el backend procesa el quitado de fondo. */
  processing: boolean;
  error: string | null;
  onSetMode: (mode: WatermarkLogoMode) => void;
}

// Media luna: círculo con la mitad rellena (mismo ícono que el mockup aprobado).
function HalfCircleIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className={className}>
      <circle cx="12" cy="12" r="10" />
      <path d="M12 18a6 6 0 0 0 0-12v12z" fill="currentColor" stroke="none" />
    </svg>
  );
}

export function LogoControls({ api, mode, processing, error, onSetMode }: Props) {
  const logo = api.scene.layers.find((l) => l.id === 'wm-logo');
  if (!logo) return null;

  const tgl = (on: boolean) =>
    `flex-1 h-10 rounded-md flex items-center justify-center gap-1.5 text-[13px] font-medium transition-colors ${
      on
        ? 'bg-brand-primary text-white'
        : 'bg-surface-card border border-neutral-200 text-neutral-600 hover:bg-surface-elevated'
    }`;

  return (
    <div className="flex flex-col gap-3 w-full">
      <OpacitySlider
        value={logo.opacity ?? 1}
        onChange={(v) => api.updateLayer('wm-logo', { opacity: v })}
        onCommit={() => api.checkpoint()}
      />
      <div className="flex gap-2">
        <button
          type="button"
          className={tgl(mode === 'no-bg')}
          disabled={processing}
          onClick={() => onSetMode(mode === 'no-bg' ? 'original' : 'no-bg')}
        >
          {processing ? (
            <Loader2 className="w-3.5 h-3.5 animate-spin" />
          ) : (
            <Eraser className="w-3.5 h-3.5" />
          )}
          Quitar fondo
        </button>
        <button
          type="button"
          className={tgl(mode === 'white')}
          disabled={processing}
          onClick={() => onSetMode(mode === 'white' ? 'original' : 'white')}
        >
          <HalfCircleIcon className="w-3.5 h-3.5" />
          Solo blanco
        </button>
      </div>
      {error && <span className="text-[12px] text-error">{error}</span>}
    </div>
  );
}
